import {useEffect, useRef} from 'react';

/**
 * The initial Mapbox style is loaded by `getSyncMapboxStyle`, which returns a `SyncPromise`-ish
 * array. The style initially returned is used to initialize the map. Then, when the `SyncPromise`
 * "resolves", the complete style is synced to the map via the `useUpdateMapboxStyle` hook.
 *
 * Once the map finishes loading that style, it invokes any 'styledata' event listeners, which is
 * the signal that the mapbox style is finally, truly loaded.
 *
 * This hook waits for the SyncPromise to load, then waits for the map's 'styledata' event, before
 * invoking the `onStyleLoaded` callback.
 */
export function useOnMapboxStyleLoaded(
    map: MapboxMap | undefined,
    hasInitialMapboxStyleLoaded: boolean,
    onStyleLoaded: ((map: MapboxMap) => void) | undefined
) {
    const hasActualMapboxStyleLoaded = useRef(false);

    useEffect(() => {
        if (!map) return;
        if (!hasInitialMapboxStyleLoaded) return;
        if (!onStyleLoaded) return;

        if (hasActualMapboxStyleLoaded.current) return;

        const onStyleData = () => {
            hasActualMapboxStyleLoaded.current = true;
            onStyleLoaded(map);
        };

        map.once('styledata', onStyleData);

        return () => {
            map.off('styledata', onStyleData);
        };
    }, [map, hasInitialMapboxStyleLoaded, onStyleLoaded]);
}
